import * as Y from 'yjs';

import {
    Block,
    Data,
    Document,
    Fragment,
    type FragmentMap,
    Inline,
    Leaf,
    Mark,
    Text,
} from '@gitbook/slate';
import { Set as ISet, List, OrderedMap } from '@gitbook/util-immutable';

import { memoYjsTransform } from '../utils';
import type {
    YSlateAbstractNode,
    YSlateBlock,
    YSlateData,
    YSlateDocument,
    YSlateFragment,
    YSlateInline,
    YSlateText,
} from './utils';

/**
 * Create a Slate document from a Yjs map.
 */
export const yToDocument = memoYjsTransform((yNode: YSlateDocument): Document => {
    const data = yNode.getAttribute('data');

    return Document.create({
        key: yNode.getAttribute('key'),
        // @ts-expect-error
        nodes: yChildrenToNodes(yNode),
        data: data ? yMapToData(data) : undefined,
    });
});

const yChildrenToNodes = memoYjsTransform(
    (
        element: YSlateDocument | YSlateBlock | YSlateInline | YSlateFragment
    ): List<Block | Inline | Text | Fragment> => {
        return List.create(
            element.toArray().map((node) => {
                if (node instanceof Y.XmlElement) {
                    return yNodeToNode(node);
                }
                if (node instanceof Y.XmlText) {
                    return yTextToText(node);
                }
                throw new Error(`Unknown node type: ${node}`);
            })
        );
    }
);

const yTextToText = memoYjsTransform((yText: YSlateText): Text => {
    const deltas = yText.toDelta();
    const leaves = deltas.map((delta) => {
        return Leaf.create({
            text: delta.insert,
            marks: yAttributesToMarks(delta.attributes || {}),
        });
    });

    return Text.create({
        key: yText.getAttribute('key'),
        leaves: List.create(leaves),
    });
});

const yNodeToNode = memoYjsTransform(
    (yNode: YSlateBlock | YSlateInline | YSlateFragment): Block | Inline | Fragment => {
        const key = (yNode as YSlateAbstractNode).getAttribute('key');

        switch (yNode.nodeName) {
            case 'block': {
                const yBlock = yNode as YSlateBlock;
                const data = yBlock.getAttribute('data');
                const fragments = yBlock.getAttribute('fragments');
                return Block.create({
                    key,
                    type: yBlock.getAttribute('type'),
                    isVoid: yBlock.getAttribute('isVoid'),
                    data: data ? yMapToData(data) : undefined,
                    // @ts-expect-error
                    nodes: yChildrenToNodes(yNode),
                    fragments: fragments ? yArrayToFragments(fragments) : undefined,
                });
            }
            case 'inline': {
                const yInline = yNode as YSlateInline;
                const data = yInline.getAttribute('data');
                const fragments = yInline.getAttribute('fragments');
                return Inline.create({
                    key,
                    type: yInline.getAttribute('type'),
                    isVoid: yInline.getAttribute('isVoid'),
                    data: data ? yMapToData(data) : undefined,
                    // @ts-expect-error
                    nodes: yChildrenToNodes(yNode),
                    fragments: fragments ? yArrayToFragments(fragments) : undefined,
                });
            }
            case 'fragment': {
                const yFragment = yNode as YSlateFragment;
                return Fragment.create({
                    key,
                    type: yFragment.getAttribute('type'),
                    fragment: yFragment.getAttribute('fragment'),
                    // @ts-expect-error
                    nodes: yChildrenToNodes(yNode),
                });
            }
            default: {
                throw new Error(`Unknown object type: ${yNode.nodeName}`);
            }
        }
    }
);

const yArrayToFragments = memoYjsTransform((array: Y.XmlFragment): FragmentMap => {
    return OrderedMap.create(
        array.toArray().map((node) => {
            if (!(node instanceof Y.XmlElement)) {
                throw new Error(`Unknown node type: ${node}`);
            }

            const fragment = yNodeToNode(node) as Fragment;
            return [fragment.fragmentKey, fragment] as [string, Fragment];
        })
    );
});

function yAttributesToMarks(attributes: { [key: string]: any }): ISet<Mark> {
    return ISet.create(
        Object.keys(attributes).map((key) => Mark.create({ type: key, data: attributes[key] }))
    );
}

const yMapToData = memoYjsTransform((map: YSlateData): Data => {
    // @ts-expect-error
    return Data.create(map.toJSON());
});
